import { InfoTip } from "@/components/InfoTip";
import { formatNumber, formatYYYYMM, monthsAgoFromYYYYMM } from "@/lib/format";
import { TIPS } from "@/lib/glossary";
import type { Tank } from "@/types/api";

const TANK_CLEANING_RED_MONTHS = 60; // matches backend TANK_CLEANING_HIGH_MONTHS

interface Props {
  tanks: Tank[];
  // System-level PWS_DESIGN_CAPACITY — FDEP publishes no per-plant
  // capacity, so hours-of-storage divides by the system design rate.
  designCapacityGpd: number | null;
}

export function StorageCard({ tanks, designCapacityGpd }: Props): JSX.Element {
  const totalStorage = tanks.reduce(
    (sum, t) => sum + (t.capacity_gal ?? 0),
    0,
  );
  const hoursOfCapacity =
    designCapacityGpd !== null && designCapacityGpd > 0
      ? (totalStorage * 24) / designCapacityGpd
      : null;

  return (
    <section className="card overflow-x-auto">
      <h2 className="card-title">Storage</h2>
      <p className="text-xs text-ink-400 mb-3">
        Finished-water tanks and how much storage is on hand.
      </p>
      {tanks.length === 0 ? (
        <p className="text-sm text-ink-300">No tanks on record.</p>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead className="text-left text-ink-300 border-b border-ink-700">
              <tr>
                <th className="py-2 pr-4 font-medium">Tank</th>
                <th className="py-2 pr-4 font-medium">Type<InfoTip {...TIPS.tankType} /></th>
                <th className="py-2 pr-4 font-medium">Capacity (gal)<InfoTip {...TIPS.tankCapacity} /></th>
                <th className="py-2 pr-4 font-medium">Coating<InfoTip {...TIPS.coating} /></th>
                <th className="py-2 font-medium">Last cleaned<InfoTip {...TIPS.lastCleaned} /></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-700">
              {tanks.map((t) => {
                const months = monthsAgoFromYYYYMM(t.last_cleaned);
                const overdue = months !== null && months > TANK_CLEANING_RED_MONTHS;
                return (
                  <tr key={t.name}>
                    <td className="py-2 pr-4 font-medium">{t.name}</td>
                    <td className="py-2 pr-4">{t.type ?? "—"}</td>
                    <td className="py-2 pr-4 font-mono">{formatNumber(t.capacity_gal)}</td>
                    <td className="py-2 pr-4">{t.coating ?? "—"}</td>
                    <td className={`py-2 ${overdue ? "text-severity-high font-medium" : ""}`}>
                      {formatYYYYMM(t.last_cleaned)}
                      {months !== null && (
                        <span className="ml-2 text-xs text-ink-400">({months} mo)</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="mt-4 grid grid-cols-2 gap-4 text-sm border-t border-ink-700 pt-3">
            <div>
              <dt className="text-ink-300 inline-flex items-center">Total storage<InfoTip {...TIPS.totalStorage} /></dt>
              <dd className="font-mono">{formatNumber(totalStorage)} gal</dd>
            </div>
            <div>
              <dt className="text-ink-300 inline-flex items-center">Hours at max design output<InfoTip {...TIPS.hoursMaxOutput} /></dt>
              <dd className="font-mono">
                {hoursOfCapacity !== null ? hoursOfCapacity.toFixed(1) + " h" : "—"}
              </dd>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
